import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {NgForm} from '@angular/forms';
import {EditComponent} from 'web-shared';
import {ActivatedRoute, Router} from '@angular/router';
import {BaseConnectionModel} from 'web-shared';
import {GLOBAL, LeModalService, UserModel, UsersQuery} from 'common-shared';
import {ApiResponse} from 'common-shared';
import {PropertyMappingModel} from 'web-shared';
import {ClassificatorValuesQuery} from 'common-shared';
import {StateService} from "web-shared";
import {Observable, Subject} from "rxjs";
import {takeUntil} from "rxjs/operators";
import {TranslocoWithKeyService} from "web-shared";
import {marker} from "@jsverse/transloco-keys-manager/marker";
import {UserService} from "web-shared";
import {ResourceEditModalComponent} from "../resource-edit-modal/resource-edit-modal.component";
import {SortEvent} from "primeng/api";
import {SpecLinesEditComponent} from "../../../common/spec/spec-lines-edit/spec-lines-edit.component";
import {ResourceConnectionModel, ResourceModel} from "../models/resource.model";
import {ResourceQuery} from "../models/resource.query";
import {ResourceService} from "../resource.service";

@Component({
  selector: 'mes-web-resource-edit',
  templateUrl: './resource-edit.component.html'
})
export class ResourceEditComponent extends EditComponent implements OnInit {
  @Input() resource: ResourceModel;
  @Output() changed = new EventEmitter<boolean>();
  resources: ResourceQuery[] = [];
  @Input() response: ApiResponse<any>;
  @ViewChild(SpecLinesEditComponent, {static: false}) specLinesEditComponent: SpecLinesEditComponent;
  @ViewChild(NgForm, {static: true}) form: NgForm;
  showPropertyLocalizations: boolean = false;
  @Input() shouldResetSaveState$: Subject<boolean> = new Subject<boolean>();
  @Input() wasValidated$ = new Subject<boolean>();
  toConnectionTitle$: Observable<string>;
  fromConnectionTitle$: Observable<string>;
  toConnectionFirstRow: number = 0;
  fromConnectionFirstRow: number = 0;
  toConnectionRowsPerPage: number = 10;
  fromConnectionRowsPerPage: number = 10;
  global = GLOBAL;

  constructor(private route: ActivatedRoute, private router: Router, public classificatorValueService: ClassificatorValuesQuery, public usersQuery: UsersQuery, public resourceService: ResourceService, public stateService: StateService, private translocoWithKeyService: TranslocoWithKeyService, private userService: UserService, private modalService: LeModalService) {
    super();
  }

  ngOnInit() {
    this.form.statusChanges.pipe(takeUntil(this.componentDestroyed)).subscribe(() => {
      this.changed.emit(this.form.pristine);
    });

    this.shouldResetSaveState$.pipe(takeUntil(this.componentDestroyed)).subscribe(() => this.setFormPristine());

    this.resourceService.getAll$.pipe(takeUntil(this.componentDestroyed)).subscribe(value => {
      this.resources = value;
    });

    this.ensureUser();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.resource) {
      this.toConnectionTitle$ = this.translocoWithKeyService.selectTranslate(this.resourceService.getToConnectionTitle(this.resource));
      this.fromConnectionTitle$ = this.translocoWithKeyService.selectTranslate(this.resourceService.getFromConnectionTitle(this.resource));
      this.ensureUser();
    }
  }

  ensureUser() {
    if (this.resource.UserId) {
      this.userService.loadOne(this.resource.UserId).pipe(takeUntil(this.componentDestroyed)).subscribe(user => this.resource.User = user);
    } else {
      this.resource.User = this.resource.User ?? UserModel.CreateDefault();
    }
  }

  setFormPristine(): void {
    Object.keys(this.form.controls).forEach(control => {
      this.form.controls[control].markAsPristine();
    });
    this.changed.emit(this.form.pristine);
  }

  setChanged() {
    this.form.controls?.code?.markAsDirty();
    this.changed.emit(this.form.pristine);
  }

  setDateRange(dateRange: {From: Date, To: Date}, entity: ResourceConnectionModel) {
    if (entity.FromDate !== dateRange?.From || entity.ToDate !== dateRange?.To) {
      this.setChanged();
    }
    entity.FromDate = dateRange ? dateRange.From : null;
    entity.ToDate = dateRange ? dateRange.To : null;
  }

  onPropertyMappingChanged($event: PropertyMappingModel) {
    $event.MandatoryClv = GLOBAL.Classificators.Mandatory.Hidden;
    this.setChanged();
  }

  onPropertyMappingRemoved($event: PropertyMappingModel) {
    this.resource.ProcedureItemProperties = this.resource.ProcedureItemProperties.filter(value => value.Id !== $event.Id);
    this.setChanged();
  }

  getResourceType(connection: ResourceConnectionModel) {
    if (connection.TypeClv === GLOBAL.Classificators.ResourceConnectionType.Hierarchy) {
      return marker('ime.manufacturing.resources.Parent resource');
    }
    if (connection.TypeClv === GLOBAL.Classificators.ResourceConnectionType.Skill) {
      return marker('ime.manufacturing.resources.Skill');
    }
    if (connection.TypeClv === GLOBAL.Classificators.ResourceConnectionType.Machine) {
      return marker('ime.manufacturing.resources.Machine');
    }
    if (connection.TypeClv === GLOBAL.Classificators.ResourceConnectionType.Area) {
      return marker('ime.manufacturing.resources.Area');
    }
    return marker('ime.manufacturing.resources.Resource');
  }

  getResourceTypeLabel(connection: ResourceConnectionModel): Observable<string> {
    return this.translocoWithKeyService.selectTranslate(this.getResourceType(connection));
  }

  removeConnection(resourceConnectionModel: ResourceConnectionModel, type: 'To' | 'From') {
    if (type === 'To') {
      this.resource.ToResourceConnections = this.resource.ToResourceConnections.filter(value => value !== resourceConnectionModel);
    } else {
      this.resource.FromResourceConnections = this.resource.FromResourceConnections.filter(value => value !== resourceConnectionModel);
    }
    this.setChanged();
  }

  addConnection(type: 'To' | 'From') {
    let newConnection = new ResourceConnectionModel({$isNew: true});
    if (type === 'To') {
      newConnection.ToResourceId = this.resource.Id;
      this.resource.ToResourceConnections = [...this.resource.ToResourceConnections, newConnection];
      this.toConnectionFirstRow = BaseConnectionModel.getFirstRowNrOfLastPage(this.resource.ToResourceConnections.length, this.toConnectionRowsPerPage);
    } else {
      newConnection.FromResourceId = this.resource.Id;
      this.resource.FromResourceConnections = [...this.resource.FromResourceConnections, newConnection];
      this.fromConnectionFirstRow = BaseConnectionModel.getFirstRowNrOfLastPage(this.resource.FromResourceConnections.length, this.fromConnectionRowsPerPage);
    }

    this.setChanged();
  }

  initialize(resource: ResourceModel) {
    this.resource = resource;
  }

  openResource(resourceId: string) {
    this.modalService.show(ResourceEditModalComponent, {initialState: {entityId: resourceId} as ResourceEditModalComponent, class: 'modal-xl'});
  }

  trackByFunction = (index, item) => {
    return item.Id;
  }

  customSort(event: SortEvent) {
    if (event.field === 'TypeClv') {
      this.classificatorValueService.getAll$.pipe(takeUntil(this.componentDestroyed)).subscribe(clvs => {
        let helperColumn = 'Type';
        event.data.map(data => {
          data[helperColumn] = clvs.find(clv => clv.Id === data[event.field])?.DisplayName;
        });
        event.field = helperColumn;
        BaseConnectionModel.sort(event);
      });
    } else if (event.field === 'ToResourceId' || event.field === 'FromResourceId') {
      let helperColumn = 'Resource';
      event.data.map(data => {
        data[helperColumn] = this.resources.find(resource => resource.Id === data[event.field])?.DisplayName;
      });
      event.field = helperColumn;
      BaseConnectionModel.sort(event);
    } else {
      BaseConnectionModel.sort(event);
    }
  }
}
